import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../../utils/apiFetch";
import { FONT, SECTIONS } from "./constants";
import { C } from "./styles";
import { authHeaders } from "./helpers";
import type { CountryCode } from "./types";

import CompanyDetailsSection from "./sections/CompanyDetailsSection";
import PayScheduleSection from "./sections/PayScheduleSection";
import TaxRegistrationSection from "./sections/TaxRegistrationSection";
import BankAccountSection from "./sections/BankAccountSection";
import PayTypesSection from "./sections/PayTypesSection";
import WorkLocationsSection from "./sections/WorkLocationsSection";
import StatHolidayMethodSection from "./sections/StatHolidayMethodSection";
import ReviewSection from "./sections/ReviewSection";

const CompanyAny = CompanyDetailsSection as any;
const TaxAny = TaxRegistrationSection as any;
const ReviewAny = ReviewSection as any;

export default function PayrollSettings() {
  const navigate = useNavigate();
  const { section } = useParams();
  const activeId: string = (SECTIONS.find((s: any) => s.id === section)?.id as string) || "company";

  const [businessCountry, setBusinessCountry] = useState<CountryCode | string>("CA");

  useEffect(() => {
    apiFetch("/api/v1/company/profile", { headers: authHeaders() })
      .then((r: Response) => r.ok ? r.json() : null)
      .then((d: any) => { if (d && d.country) setBusinessCountry(d.country); })
      .catch(() => {});
  }, []);

  const renderSection = () => {
    switch (activeId) {
      case "company": return <CompanyAny businessCountry={businessCountry} setBusinessCountry={setBusinessCountry} />;
      case "schedule": return <PayScheduleSection />;
      case "tax": return <TaxAny businessCountry={businessCountry} setBusinessCountry={setBusinessCountry} />;
      case "bank": return <BankAccountSection />;
      case "items": return <PayTypesSection businessCountry={businessCountry} />;
      case "locations": return <WorkLocationsSection businessCountry={businessCountry} />;
      case "statholiday":
      case "stat-holiday":
      case "statHoliday":
        return <StatHolidayMethodSection />;
      case "review":
      case "final":
        return <ReviewAny title="Final review" />;
      default: return <CompanyAny businessCountry={businessCountry} setBusinessCountry={setBusinessCountry} />;
    }
  };

  return (
    <div style={{ maxWidth: "100%", margin: 0, padding: "28px 32px 90px", fontFamily: FONT, background: "#F4F6F8", minHeight: "100vh" }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.ink, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>Payroll</div>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: C.ink, letterSpacing: "-0.015em", margin: 0 }}>Settings</h1>
        <p style={{ fontSize: 14, color: C.ink, marginTop: 6, lineHeight: 1.55 }}>Configure company details, pay schedules, tax, bank, items, locations, and stat holidays for payroll.</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 28, alignItems: "flex-start" }}>

        <nav style={{ background: "#fff", border: "1.5px solid " + C.ink, borderRadius: 10, padding: 10, position: "sticky", top: 20 }}>
          {SECTIONS.map((item: any) => {
            const Icon = item.icon;
            const isActive = item.id === activeId;
            return (
              <button
                key={item.id}
                onClick={() => navigate("/payroll/settings/" + item.id)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 11,
                  padding: "10px 12px",
                  marginBottom: 2,
                  background: isActive ? C.ink : "transparent",
                  color: isActive ? "#fff" : C.ink,
                  border: 0,
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 500,
                  cursor: "pointer",
                  fontFamily: FONT,
                  textAlign: "left",
                  transition: "background .15s",
                }}
              >
                {Icon && <Icon style={{ width: 16, height: 16, color: isActive ? "#fff" : C.ink, flex: "0 0 16px" }} />}
                <span style={{ flex: 1 }}>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <main style={{ background: "#fff", border: "1.5px solid " + C.ink, borderRadius: 10, padding: "28px 32px", minHeight: 500 }}>
          {renderSection()}
        </main>
      </div>
    </div>
  );
}